import {
  Divider, Grid, Table as MTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { FieldValue } from '../../components/common/presentation';
import { formatLocalDate } from '../date-time';
import { truncateSpan } from '../format';
import { AutoCode, CptProcedure, Diagnosis } from './document-types';

function VField({ label, value, style = undefined, truncate = NaN }) {
  return (
    <Grid container item direction="column" style={style}>
      <Grid item>
        <Typography
          style={{ whiteSpace: 'nowrap' }}
          variant="body2"
          color="textSecondary"
        >
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <Typography color="textPrimary">
          {isNaN(truncate) ? value : truncateSpan(value, truncate + 'px')}
        </Typography>
      </Grid>
    </Grid>
  );
}

export function procedureSummary(
  p: CptProcedure,
  showCodes = true,
  skipMetaData = false
): string {
  let result: string = p.code;

  p.multiplier = p.multiplier || 1;
  if (p.multiplier) {
    result += 'x' + p.multiplier;
  }

  if (p.modifiers && p.modifiers.length) {
    result += ' / ';
    result += p.modifiers.join(',');
  }

  if (skipMetaData) {
    result += ' / ';
    return result;
  } else {
    result += ' / ';

    if (showCodes) {
      if (p.diagnoses && p.diagnoses.length) {
        result += p.diagnoses.join(',');
        result += ' / ';
      }
    }
  }
  if (p.serviceDate) {
    result += `SD ${formatLocalDate(p.serviceDate)} / `;
  }

  return result;
}

const cellStyle = { paddingTop: 0, paddingBottom: 0, fontSize: 11 };
function DiagnosisTable({ rows }) {
  return (
    <Grid container item direction="column" spacing={2}>
      <Grid item>
        <Typography
          style={{ whiteSpace: 'nowrap' }}
          variant="body2"
          color="textSecondary"
        >
          Diagnosis
        </Typography>
      </Grid>
      <Grid item>
        <TableContainer
          component={(a: any) =>
            (
              <Grid
                item
                {...a}
                style={{
                  backgroundColor: 'white',
                  width: '33vw',
                  overflowX: 'hidden',
                  fontSize: 10,
                  overflowY: 'hidden',
                  border: '1px solid #ccc',
                  flexWrap: 'nowrap',
                }}
              />
            ) as any
          }
        >
          <MTable size="small" style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" style={{ width: 69 }}>
                  Code
                </TableCell>
                <TableCell align="left" style={{ width: 90 }}>
                  Confidence
                </TableCell>
                <TableCell align="left">Evidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows || []).map((a: Diagnosis) => (
                <TableRow key={a.code}>
                  <TableCell align="left" style={cellStyle}>
                    {a.code}
                  </TableCell>
                  <TableCell align="center" style={cellStyle}>
                    {a.confidence}
                  </TableCell>
                  <TableCell align="left" style={cellStyle}>
                    {a.evidence}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MTable>
        </TableContainer>
      </Grid>
    </Grid>
  );
}

function CptTable({ rows }) {
  return (
    <Grid container item direction="column" spacing={2}>
      <Grid item>
        <Typography
          style={{ whiteSpace: 'nowrap' }}
          variant="body2"
          color="textSecondary"
        >
          Procedures
        </Typography>
      </Grid>
      <Grid item>
        <TableContainer
          component={(a: any) =>
            (
              <Grid
                item
                {...a}
                style={{
                  backgroundColor: 'white',
                  width: '66vw',
                  overflowX: 'hidden',
                  fontSize: 10,
                  overflowY: 'hidden',
                  border: '1px solid #ccc',
                  flexWrap: 'nowrap',
                }}
              />
            ) as any
          }
        >
          <MTable size="small" style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" style={{ width: 69 }}>
                  Code
                </TableCell>
                <TableCell align="left" style={{ width: 14 }}>
                  Mult.
                </TableCell>
                <TableCell align="left" style={{ width: 66 }}>
                  Modifiers
                </TableCell>
                <TableCell align="left">Diagnosis</TableCell>
                <TableCell align="left" style={{ width: 114 }}>
                  Service Date
                </TableCell>
                <TableCell align="left" style={{ width: 96 }}>
                  Confidence
                </TableCell>
                <TableCell align="left">Evidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows || []).map((a: CptProcedure) => (
                <TableRow key={a.code}>
                  <TableCell align="left" style={cellStyle}>
                    {a.code}
                  </TableCell>
                  <TableCell align="left" style={cellStyle}>
                    {a.multiplier}
                  </TableCell>

                  <TableCell align="left" style={cellStyle}>
                    {(a.modifiers || []).join(',')}
                  </TableCell>

                  <TableCell align="left" style={cellStyle}>
                    {(a.diagnoses || []).join(',')}
                  </TableCell>

                  <TableCell align="left" style={cellStyle}>
                    {formatLocalDate(a.serviceDate)}
                  </TableCell>

                  <TableCell align="center" style={cellStyle}>
                    {a.confidence}
                  </TableCell>
                  <TableCell align="left" style={cellStyle}>
                    {a.evidence}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MTable>
        </TableContainer>
      </Grid>
    </Grid>
  );
}

export function AutoCodeRenderer(ctx: AutoCode) {
  const {
    patient,
    version,
    confidence,
    cptProcedures,
    diagnoses,
    status,
    statusNote,
    visit,
  } = ctx;

  const firstName = patient?.firstName || '';
  const lastName = patient?.lastName || '';
  const mrn = patient?.mrn || '';

  const { dischargeDate, serviceDate, accountNumber, department } = visit || {};
  const divider = (
    <div style={{ padding: '12px 0px 12px 0px' }}>
      <Divider orientation="horizontal" style={{ width: '100%' }} />
    </div>
  );

  const rowStyle: any = { paddingTop: 12, flexWrap: 'nowrap', width: '60vw' };

  const dx = diagnoses || [];
  const cpt = cptProcedures || [];

  return (
    <div style={{ width: '100%', height: '80vh', padding: '12px 24px 0 24px' }} id="audtocode">
      {patient && (
        <FieldValue label="Patient" value={lastName + ', ' + firstName} />
      )}
      {divider}
      <Grid
        container
        style={rowStyle}
        direction="row"
        spacing={2}
        justify="center"
        alignItems="flex-start"
      >
        <VField label="Account" value={accountNumber} />
        <VField label="MRN" value={mrn} truncate={67} />
        {dischargeDate && (
          <VField label="Discharge Date" value={formatLocalDate(dischargeDate)} />
        )}
        {serviceDate && (
          <VField label="Service Date" value={formatLocalDate(serviceDate)} />
        )}
      </Grid>

      {divider}
      <Grid
        container
        style={rowStyle}
        direction="row"
        spacing={2}
        justify="center"
        alignItems="flex-start"
      >
        <VField label="Version" value={version} />
        <VField label="Confidence" value={confidence} />
        <VField label="Status" value={status} />
        <VField
          label="Status Note"
          value={statusNote}
          style={{ paddingBottom: 24 }}
        />
      </Grid>

      <Divider orientation="horizontal" style={{ width: '100%' }} />
      <div style={{ height: '54vh', overflowX: 'hidden', overflowY: 'auto' }}>
        <Grid
          container
          style={{ paddingTop: 24, flexWrap: 'nowrap', width: '100%' }}
          direction="column"
          spacing={2}
          alignItems="flex-start"
        >
          <DiagnosisTable rows={dx} />
          <CptTable rows={cpt} />
        </Grid>
      </div>
    </div>
  );
}
